/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useContext, useEffect, useReducer, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {KTIcon} from '../../../helpers'
import {NO_DATA_TEXT, chartValueFormatter, hasAnyValue, toChartSeries} from '../../../helpers/ChartDataHelpers'
import {getCSSVariableValue} from '../../../assets/ts/_utils'
import {Dropdown1} from '../../content/dropdown/Dropdown1'
import {useThemeMode} from '../../layout/theme-mode/ThemeModeProvider'
import { BLACKBOXES_LAST5_URL,BLACKBOXES_MAX_CPU_URL } from '../../../../urls'
import { blackboxIdContext } from '../../../../app/modules/profile/components/contexts/BlackboxIdContext'
import { useAuth } from '../../../../app/modules/auth'
type Props = {
  className: string
  chartHeight: string
  chartColor: string
}

const initialState = {
  data: null,
  loading: true,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_SUCCESS':
      return { ...state, data: action.payload, loading: false };
    case 'FETCH_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

const BlackboxCpuGraph: React.FC<Props> = ({className, chartHeight, chartColor}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const currentBlackBoxId = useContext(blackboxIdContext);
  // `cpuGraph`, `loading` ve `loading1` KALDIRILDI. `cpuGraph` yalnizca olu `getMax`
  // icinde kullaniliyordu; `loading1` hic okunmuyordu; `loading` ise ekranda grafigin
  // gorunup gorunmeyecegine karar veriyordu ama onu bes saniyelik bir setTimeout da
  // yaziyordu -- yani grafik "veri geldi mi" degil "bes saniye gecti mi" sorusuna gore
  // cikiyordu. Artik yukleme durumu reducer'daki state.loading, hata ise state.error.
  const [maxCpu, setMaxCpu] = useState<any>([])
  const { currentUser,logout } = useAuth()
  const token = currentUser?.token
  const [state, dispatch] = useReducer(reducer, initialState);

  const createHeader = () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', "" + token?.toString());
    return headers
  }

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }
   
    const chart = new ApexCharts(chartRef.current, chartOptions(chartHeight, chartColor, state.data))
    if (chart) {
      chart.render()
    }

    return chart
  }

  // `getMax` KALDIRILDI: tanimliydi ama hicbir yerden cagrilmiyordu (tek benzer isim
  // `getMaxCpu`, ayri bir fonksiyon). Ustelik `return` ifadesi yoktu -- hesapladigi
  // degeri atiyordu -- ve `cpuGraph["cpu"]` tanimsiz oldugunda patlardi. `cpuGraph`
  // state'i de yalnizca orada kullaniliyordu, o da kaldirildi.

  /**
   * 1) VERİ ÇEKME. Yalnızca karakutu değiştiğinde.
   *
   * Önceden çizimle aynı effect'teydi, bağımlılık dizisi `[chartRef, mode, loading,
   * state.data]` idi ve yanında hiç temizlenmeyen bir `setTimeout(..., 5000)` vardı:
   * hata durumunda "yükleniyor"dan çıkışı sağlayan tek şey o beş saniyelik zamanlayıcıydı.
   * Her koşuda bir de `console.log("----------")` yazıyordu.
   */
  useEffect(() => {
    const blackboxId = currentBlackBoxId[0]
    if (!blackboxId) {
      return
    }
    void getGraphInfo(blackboxId)
    void getMaxCpu(blackboxId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBlackBoxId[0]])

  /**
   * 2) GRAFİK ÇİZME. Yalnızca tema ya da veri değiştiğinde.
   *
   * "Ölçüm yok" durumu `chartOptions` içinde ele alınıyor (`NO_DATA_TEXT`).
   */
  useEffect(() => {
    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.data])
  async function getGraphInfo(blackboxId: string) {
    try {
      const response = await fetch(BLACKBOXES_LAST5_URL+`?blackboxId=${blackboxId}`, {
        method: "GET",
        headers: createHeader()
      })
      // 401 kontrolu `!response.ok` kontrolunden SONRA geliyordu. 401 zaten `ok`
      // olmadigi icin ustteki satir firlatiyor ve bu dala HIC gelinmiyordu --
      // yani oturum dustugunde `logout()` cagrilmiyor, kullanici bozuk bir
      // oturumda kaliyordu. Sira duzeltildi.
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const responseData = await response.json()
      dispatch({ type: 'FETCH_SUCCESS', payload: responseData });
    } catch (error) {
      // Bu blok TAMAMEN BOSTU (tek satiri yorumdaydi). Istek patladiginda hicbir sey
      // olmuyordu: ne hata durumu, ne log. Kart basligi duruyor, grafik hic cizilmiyor
      // ve kullanici neden bos oldugunu anlayamiyordu.
      console.error('CPU verisi alinamadi:', error)
      dispatch({ type: 'FETCH_ERROR', payload: 'CPU verisi alınamadı.' });
    }
  }
  /**
   * "Max CPU kullanımı" değerini çeker.
   *
   * Önceden hiçbir denetim yoktu: ne `response.ok`, ne 401, ne `catch`. Hata gövdesi
   * `res.json()` ile ayrıştırılıp `setMaxCpu(<hata nesnesi>)` yapılıyordu; `maxCpu["maxCpu"]`
   * `undefined` olduğu için kartta **boş bir değer** ve yanında her zaman yeşil duran bir
   * yukarı ok görünüyordu.
   */
  async function getMaxCpu(blackboxId: string) {
    try {
      const response = await fetch(BLACKBOXES_MAX_CPU_URL+`?blackboxId=${blackboxId}`, {
        method: "GET",
        headers: createHeader()
      })
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setMaxCpu(await response.json())
    } catch (error) {
      console.error('Max CPU verisi alinamadi:', error)
      setMaxCpu([])
    }
  }
  return (
    <div className={`card ${className}`}>
      {/* begin::Beader */}
      <div className='card-header border-0 py-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>CPU kullanımı</span>

          <span className='text-muted fw-semibold fs-7'>Son 5 gün</span>
        </h3>

        <div className='card-toolbar'>
          {/* begin::Menu */}
          <button
            type='button'
            className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
          >
            <KTIcon iconName='category' className='fs-2' />
          </button>
          <Dropdown1 />
          {/* end::Menu */}
        </div>
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body p-0 d-flex flex-column'>
        {/* begin::Stats */}
        <div className='card-p pt-5 bg-body flex-grow-1'>
          {/* begin::Row */}
          <div className='row g-0'>
            {/* begin::Col */}
            <div className='col mr-8'>
              {/* begin::Label */}
              <div className='fs-7 text-muted fw-semibold'>Max CPU kullanımı</div>
              {/* end::Label */}

              {/* begin::Stat */}
              <div className='d-flex align-items-center'>
                {/* Deger yoksa BOS BIRAKMIYORUZ. Oncesinde maxCpu["maxCpu"] tanimsiz
                    oldugunda (hata ya da veri yok) kartta bos bir yer goruniyordu ve
                    bunun sebebi anlasilmiyordu. */}
                <div className='fs-4 fw-bold'>
                  {maxCpu["maxCpu"] != null ? maxCpu["maxCpu"] : 'bilinmiyor'}
                </div>
                {/* Yukari ok KOSULLU oldu. Oncesinde her durumda -- deger yokken ve
                    istek basarisizken de -- yesil bir "yukari" oku duruyordu; veriyi
                    temsil etmeyen, her zaman "iyi" diyen bir gosterge. */}
                {maxCpu["maxCpu"] != null && (
                  <KTIcon iconName='arrow-up' className='fs-5 text-success ms-1' />
                )}
              </div>
              {/* end::Stat */}
            </div>
            {/* end::Col */}

            {/* begin::Col */}
            <div className='col'>
              {/* begin::Label */}
              <div className='fs-7 text-muted fw-semibold'>Tarih</div>
              {/* end::Label */}

              {/* begin::Stat */}
              <div className='fs-4 fw-bold'>
                {maxCpu["date"] != null
                  ? String(maxCpu["date"]).replace('T', ' ').split('.')[0]
                  : 'bilinmiyor'}
              </div>
              {/* end::Stat */}
            </div>
            {/* end::Col */}
          </div>
          {/* end::Row */}

          {/* begin::Row */}
          
          {/* end::Row */}
        </div>
        {/* end::Stats */}

        {/* Hata artik GORUNUYOR. Oncesinde catch blogu bostu: istek patladiginda kart
            basligi duruyor, grafik hic cizilmiyor ve kullanici sebebini anlamiyordu. */}
        {state.error && <div className='card-p pb-5 text-danger'>{state.error}</div>}
        {state.loading && !state.error && (
          <div className='card-p pb-5 text-gray-500'>Yükleniyor…</div>
        )}

        {/* begin::Chart */}
        {state.data ? 
        <div
        
          ref={chartRef}
          className='mixed-widget-3-chart card-rounded-bottom'
          data-kt-chart-color={chartColor}
          style={{height: 100}}
        ></div>
        :<></>}
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

const chartOptions = (chartHeight: string, chartColor: string, data2: any): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-800')
  const strokeColor = getCSSVariableValue('--bs-gray-300')
  const baseColor = getCSSVariableValue('--bs-' + chartColor)
  const lightColor = getCSSVariableValue('--bs-' + chartColor + '-light')
  //const data1 =[40, 25, 45, 30, 55, 55]
 
  // Hicbir gun icin olcum yoksa seri bos gonderiliyor; ApexCharts bos grafik
  // yerine noData mesajini gosteriyor (M-19 devami).
  const cpu = toChartSeries(data2["cpu"])

  return {
    noData: {
      text: NO_DATA_TEXT,
      align: 'center',
      verticalAlign: 'middle',
      style: {color: labelColor, fontSize: '14px'},
    },
    series: hasAnyValue(data2["cpu"])
      ? [{name: 'Ortalama Cpu kullanımı', data: cpu}]
      : [],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: chartHeight,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: data2["date"],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        show: false,
        position: 'front',
        stroke: {
          color: strokeColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 15,
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        // Olcum yoksa deger yerine "Olcum yok" yaziyor.
        formatter: chartValueFormatter(''),
      },
    },
    colors: [lightColor],
    markers: {
      colors: [lightColor],
      strokeColors: [baseColor],
      strokeWidth: 3,
    },
  }
}

export {BlackboxCpuGraph}
